<script setup
        lang="ts"
>
import type { FilterData, FilterItem } from '@/js/types/filter.ts';
import type { RequestFilter, SelectedFilters } from './types.ts';
import { filter } from '@/js/api/catalog.ts';
import { getLocaleValue } from '@/js/helper/locale.ts';
import BaseIcon from '@/js/vue/base/base-icon.vue';
import { toRequestFilter, toSelectedFilters, toUnifiedFilter } from '@/js/vue/filter/convert.ts';

import { flip, offset, shift, useFloating } from '@floating-ui/vue';
import { computedAsync } from '@vueuse/core';
import { computed, ref, watch } from 'vue';
import AppliedFilters from './applied-filters.vue';
import FilterRange from './filter-range.vue';
import FilterVariants from './filter-variants.vue';

const props = defineProps<{
  filterData: FilterData
  categoryId: number
  appliedFilters: RequestFilter[]
}>();

const loading = ref(false);

const selectedFilters = ref<SelectedFilters>(toSelectedFilters(props.appliedFilters));

const unifiedFilters = computed(() => {
  return props.filterData.filters.map(toUnifiedFilter);
});

const request = computed(() => {
  return toRequestFilter(selectedFilters.value);
});

const response = computedAsync(async (onCancel) => {
  const abortController = new AbortController();
  onCancel(() => abortController.abort());
  return (await filter(props.categoryId, request.value, {
    signal: abortController.signal,
  }));
}, null, {
  lazy: true,
});

const lastFilter = ref<HTMLElement | null>(null);
const showFilter = ref(false);

const floating = ref(null);

const { floatingStyles } = useFloating(lastFilter, floating, {
  placement: 'right',
  middleware: [
    offset(20),
    flip(),
    shift(),
  ],
});

const requestedFilterValues = computed(() => {
  if (!response.value?.data?.filters) {
    return null;
  }

  return Object.fromEntries(response.value.data.filters.map(
    filter => [
      filter.id ?? filter.type,
      filter,
    ],
  ));
});

const displayFilters = computed(() => {
  return unifiedFilters.value
    .map((filter) => {
      if (!requestedFilterValues.value) {
        return filter;
      }

      const requestedFilter = requestedFilterValues.value[filter.key] as FilterItem | undefined;

      if ('items' in filter) {
        return {
          ...filter,
          items: filter.items.map((item) => {
            let count = 0;
            if (requestedFilter && requestedFilter.items) {
              const requestedItem = requestedFilter.items.find(reqItem => reqItem.id === item.id);
              count = requestedItem?.count ?? 0;
            }
            return {
              ...item,
              count,
            };
          }),
        };
      }

      if ('min' in filter && 'max' in filter) {
        return {
          ...filter,
          min: requestedFilter?.from || filter.min,
          max: requestedFilter?.to || filter.max,
        };
      }

      throw new Error(`Unknown filter type: ${filter.type}`);
    });
});

watch(response, (data) => {
  if (data?.data) {
    if (lastFilter.value) {
      showFilter.value = true;
    }
  }
});

const total = computed(() => {
  return response.value?.data?.total || 0;
});

const link = computed(() => {
  return response.value?.data?.url || null;
});

function saveLastClickPosition(event: MouseEvent) {
  lastFilter.value = (event.target as HTMLElement).closest('.js-floating-container');
  showFilter.value = false;
}

function reset() {
  selectedFilters.value = {};
  lastFilter.value = null;
  showFilter.value = false;
}

function applySelectedFilters(filters: SelectedFilters) {
  selectedFilters.value = filters;
}
</script>

<template>
  <div
    class="filters"
    :data-loading="loading"
    @click="saveLastClickPosition"
  >
    <label
      class="base-button base-button_type_secondary filters__btn-open"
      for="filters-modal-control"
    >
      <BaseIcon
        type="main"
        icon="settings"
        :width="24"
        :height="24"
      />
      {{ getLocaleValue('filters') }}
    </label>
    <input
      id="filters-modal-control"
      type="checkbox"
      class="filters__modal-control"
      hidden
    >
    <div class="filters__popup-main">
      <div class="filters__heading-container">
        <h2 class="filters__heading">
          {{ getLocaleValue('filters') }}
        </h2>
        <label
          class="filters__btn-close base-button"
          for="filters-modal-control"
          aria-label="close"
        >
          <BaseIcon
            type="main"
            icon="close"
            :width="24"
            :height="24"
          />
        </label>
      </div>
      <div class="filters__content">
        <AppliedFilters
          :filters="unifiedFilters"
          :applied-filters="selectedFilters"
          @update="applySelectedFilters"
        />
        <div
          v-for="(item) in displayFilters"
          :key="item.key"
          class="filters__details-utils"
        >
          <FilterRange
            v-if="item.type === 'range'"
            v-model:range="selectedFilters[item.key]"
            :name="item.name"
            :min="item.min"
            :max="item.max"
            :open-by-default="item.open"
          />
          <FilterVariants
            v-else-if="item.type === 'variants'"
            v-model:values="selectedFilters[item.key]"
            :name="item.name"
            :options="item.items"
            :open-by-default="item.open"
          />
        </div>
      </div>
    </div>
    <div
      v-if="showFilter && link"
      ref="floating"
      class="filters__floating-button"
      :style="floatingStyles"
    >
      <a
        :href="link"
        class="base-button base-button_type_primary"
      >
        {{ getLocaleValue('show') }} {{ $t("products", total) }}
      </a>
    </div>
    <div class="filters__popup-footer">
      <div class="filters__footer">
        <a
          v-if="link"
          class="base-button base-button_type_primary"
          :href="link"
        >
          {{ getLocaleValue('show') }} {{ $t("products", total) }}
        </a>
        <button
          class="base-button base-button_type_ghost js-floating-container"
          type="button"
          @click="reset"
        >
          {{ getLocaleValue('reset_filters') }}
        </button>
      </div>
    </div>
  </div>
</template>

<style lang="scss"></style>
